/**
 * Epic Database Operations
 *
 * CRUD operations for epic/initiative management.
 */

import { supabase } from '@/lib/supabaseClient';
import { withRetry } from '@/utils/retry';
import { handleNotFoundError, buildUpdatePayload } from './helpers';

export type EpicStatus = 'open' | 'in_progress' | 'completed' | 'cancelled';

export interface Epic {
  id: string;
  name: string;
  spaceId: string;
  description: string | null;
  status: EpicStatus;
  targetDate: number | null;
  color: string | null;
  progress: number;
  createdAt: string;
  updatedAt: string;
}

export interface EpicSummary extends Epic {
  totalTasks: number;
  completedTasks: number;
  progressPercent: number;
}

export interface CreateEpicData {
  name: string;
  spaceId: string;
  description?: string;
  status?: EpicStatus;
  targetDate?: number | null;
  color?: string;
}

export interface UpdateEpicData {
  name?: string;
  description?: string;
  status?: EpicStatus;
  targetDate?: number | null;
  color?: string;
  progress?: number;
}

// Supabase row type for epics table
interface SupabaseEpicRow {
  id: string;
  name: string;
  space_id: string;
  description: string | null;
  status: EpicStatus;
  target_date: number | null;
  color: string | null;
  progress: number | null;
  created_at: string;
  updated_at: string;
}

// Supabase row type for epic_summary view
interface SupabaseEpicSummaryRow extends SupabaseEpicRow {
  total_tasks: number;
  completed_tasks: number;
  progress_percent: number;
}

// Convert from Supabase (snake_case) to Epic (camelCase)
function convertEpicFromSupabase(row: SupabaseEpicRow): Epic {
  return {
    id: row.id,
    name: row.name,
    spaceId: row.space_id,
    description: row.description,
    status: row.status,
    targetDate: row.target_date,
    color: row.color,
    progress: row.progress || 0,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function convertEpicSummaryFromSupabase(row: SupabaseEpicSummaryRow): EpicSummary {
  return {
    ...convertEpicFromSupabase(row),
    totalTasks: row.total_tasks || 0,
    completedTasks: row.completed_tasks || 0,
    progressPercent: row.progress_percent || 0,
  };
}

/**
 * Get all epics for a space
 */
export async function getEpicsBySpace(spaceId: string): Promise<Epic[]> {
  const { data, error } = await withRetry(() =>
    supabase
      .from('epics')
      .select('*')
      .eq('space_id', spaceId)
      .order('created_at', { ascending: false })
  );

  if (error) throw error;
  return (data || []).map(convertEpicFromSupabase);
}

/**
 * Get epics with summary (task counts, progress)
 */
export async function getEpicSummaries(spaceId: string): Promise<EpicSummary[]> {
  const { data, error } = await withRetry(() =>
    supabase
      .from('epic_summary')
      .select('*')
      .eq('space_id', spaceId)
      .order('created_at', { ascending: false })
  );

  if (error) throw error;
  return (data || []).map(convertEpicSummaryFromSupabase);
}

/**
 * Get a single epic by ID
 */
export async function getEpic(id: string): Promise<Epic | null> {
  const { data, error } = await withRetry(() =>
    supabase
      .from('epics')
      .select('*')
      .eq('id', id)
      .single()
  );

  if (error) return handleNotFoundError(error, null);
  return convertEpicFromSupabase(data);
}

/**
 * Create a new epic
 */
export async function createEpic(data: CreateEpicData): Promise<Epic> {
  const insertData = {
    name: data.name,
    space_id: data.spaceId,
    description: data.description || null,
    status: data.status || 'open',
    target_date: data.targetDate || null,
    color: data.color || null,
  };

  const { data: created, error } = await withRetry(() =>
    supabase
      .from('epics')
      .insert(insertData)
      .select()
      .single()
  );

  if (error || !created) throw error || new Error('Failed to create epic');
  return convertEpicFromSupabase(created);
}

/**
 * Update an epic
 */
export async function updateEpic(id: string, updates: UpdateEpicData): Promise<Epic> {
  const updatePayload = buildUpdatePayload(updates as Record<string, unknown>, {
    name: 'name',
    description: 'description',
    status: 'status',
    targetDate: 'target_date',
    color: 'color',
    progress: 'progress',
  });

  const { data, error } = await withRetry(() =>
    supabase
      .from('epics')
      .update(updatePayload)
      .eq('id', id)
      .select()
      .single()
  );

  if (error || !data) throw error || new Error('Failed to update epic');
  return convertEpicFromSupabase(data);
}

/**
 * Delete an epic
 */
export async function deleteEpic(id: string): Promise<boolean> {
  const { error } = await withRetry(() =>
    supabase
      .from('epics')
      .delete()
      .eq('id', id)
  );

  if (error) throw error;
  return true;
}

/**
 * Assign a task to an epic
 */
export async function assignTaskToEpic(taskId: string, epicId: string | null): Promise<void> {
  const { error } = await withRetry(() =>
    supabase
      .from('tasks')
      .update({ epic_id: epicId })
      .eq('id', taskId)
  );

  if (error) throw error;
}

/**
 * Bulk assign tasks to an epic
 */
export async function bulkAssignTasksToEpic(taskIds: string[], epicId: string | null): Promise<void> {
  const { error } = await withRetry(() =>
    supabase
      .from('tasks')
      .update({ epic_id: epicId })
      .in('id', taskIds)
  );

  if (error) throw error;
}

/**
 * Get tasks in an epic
 */
export async function getEpicTasks(epicId: string): Promise<string[]> {
  const { data, error } = await withRetry(() =>
    supabase
      .from('tasks')
      .select('id')
      .eq('epic_id', epicId)
  );

  if (error) throw error;
  return (data || []).map(t => t.id);
}

/**
 * Calculate and update epic progress based on task completion
 */
export async function updateEpicProgress(epicId: string): Promise<Epic> {
  // Get epic summary which calculates progress
  const { data, error } = await withRetry(() =>
    supabase
      .from('epic_summary')
      .select('progress_percent')
      .eq('id', epicId)
      .single()
  );

  if (error || !data) throw error || new Error('Failed to get epic summary');

  // Update epic with calculated progress
  return updateEpic(epicId, { progress: data.progress_percent });
}
